import { i18nLocales, Locales } from "@/i18n/i18nConfig";
import { DefinedLocale } from "@/i18n/locale-definitions";
import { WebItemCategoryGroup, WebItemCategoryGroupProjection } from "@/autogenerated/model";

export function getPathByLocale(locale: string, itemCategoryGroup: WebItemCategoryGroup | WebItemCategoryGroupProjection | undefined | null): string {
	return itemCategoryGroup?.languages.find((language) => language.language === locale)?.path ?? "";
}

export function getItemCategoryGroupTitleByLocale(locale: string, ItemCategoryGroup: WebItemCategoryGroup | undefined | null): string {
	const metadata = ItemCategoryGroup?.languages.find((x) => x.language === locale);
	return metadata?.title || "";
}

export function getCurrentItemGroupCategoryByPath(path: string, allCategories: WebItemCategoryGroupProjection[] | undefined | null) {
	const pathArray = path.split("/");
	const ItemCategoryGroupFromPath = pathArray.slice().filter((item) => item !== "");
	const locale = ItemCategoryGroupFromPath[0];
	const ItemCategoryGroup = ItemCategoryGroupFromPath.slice(1).join("/");

	return allCategories?.find((category) => getPathByLocale(locale, category) === ItemCategoryGroup);
}

// The allowed ids are level-1 categories: a segment is shown when it holds one, and a deeper group when it sits under one.
export function isItemCategoryGroupAllowed(itemCategoryGroupId: string, allowedItemCategoryGroupIds: string[] | null | undefined): boolean {
	if (!allowedItemCategoryGroupIds) {
		return true;
	}

	return allowedItemCategoryGroupIds.some(
		(allowedId) => allowedId === itemCategoryGroupId || allowedId.startsWith(`${itemCategoryGroupId}/`) || itemCategoryGroupId.startsWith(`${allowedId}/`),
	);
}

export function getSegmentByPath(path: string) {
	const pathArray = path.split("/");
	return pathArray[2] || null;
}

export function getCategoryByPath(path: string) {
	const pathArray = path.split("/");
	return pathArray[3] || null;
}

export function getSubCategoryByPath(path: string) {
	const pathArray = path.split("/");
	return pathArray[4] || null;
}

export function getProductGroupByPath(path: string) {
	const pathArray = path.split("/");
	return pathArray[5] || null;
}

export function getProductNameByPath(path: string) {
	const pathArray = path.split("/");
	return pathArray[6] || null;
}

export const trimMetaTitleString = (input: string): string => {
	const MAX_META_TITLE_LENGTH = 55;
	return input.length > MAX_META_TITLE_LENGTH ? input.slice(0, MAX_META_TITLE_LENGTH).trim() + "..." : input;
};

export const trimMetaDescriptionString = (input: string): string => {
	const MAX_META_DESCRIPTION_LENGTH = 160;
	return input.length > MAX_META_DESCRIPTION_LENGTH ? input.slice(0, MAX_META_DESCRIPTION_LENGTH).trim() + "..." : input;
};

export function formatSeoUrlSegment(urlSegment: string | null | undefined): string {
	if (!urlSegment) {
		return "";
	}
	// Replace specific Scandinavian, German, and other characters with their respective replacements
	urlSegment = urlSegment
		.replace(/ø/g, "oe")
		.replace(/å/g, "aa")
		.replace(/æ/g, "ae")
		.replace(/ü/g, "ue")
		.replace(/ä/g, "ae")
		.replace(/ö/g, "oe")
		.replace(/é/g, "e")
		.replace(/è/g, "e")
		.replace(/ê/g, "e")
		.replace(/ë/g, "e")
		.replace(/á/g, "a")
		.replace(/à/g, "a")
		.replace(/â/g, "a")
		.replace(/ã/g, "a")
		.replace(/ç/g, "c")
		.replace(/í/g, "i")
		.replace(/ì/g, "i")
		.replace(/î/g, "i")
		.replace(/ï/g, "i")
		.replace(/ñ/g, "n")
		.replace(/ó/g, "o")
		.replace(/ò/g, "o")
		.replace(/ô/g, "o")
		.replace(/õ/g, "o")
		.replace(/ú/g, "u")
		.replace(/ù/g, "u")
		.replace(/û/g, "u")
		.replace(/[^\w]/g, "-");

	// Replace multiple consecutive hyphens with a single hyphen
	urlSegment = urlSegment.replace(/-+/g, "-");

	// Remove leading and trailing hyphens
	urlSegment = urlSegment.replace(/^-|-$/g, "");

	return encodeURIComponent(urlSegment.toLowerCase());
}

export const getItemPath = (itemId: string, brandOrTitle: string, description?: string) => {
	const brandAndDescription = formatSeoUrlSegment(`${brandOrTitle} ${description ?? ""}`);

	return `/item/${brandAndDescription}/${itemId}`;
};

// The locale-less /item/ path redirects by the visitor's language, so links carry the page's own locale.
export const getItemLink = (locale: Locales, itemId: string, brandOrTitle: string, description?: string) => {
	return `/${locale}${getItemPath(itemId, brandOrTitle, description)}`;
};

type ContentPageSlug =
	| "termsOfService"
	| "advertising"
	| "privacyPolicy"
	| "business"
	| "businessTerms"
	| "termsOfCompetition"
	| "press"
	| "contact"
	| "expertTerms"
	| "tradeTerms"
	| "buyerProtection";

// This is a temporary solution, so the LanguageChanger works
export const contentPageSlugMap: Record<ContentPageSlug, Record<DefinedLocale, string>> = {
	termsOfService: {
		da: "brugervilkaar",
		en: "terms-of-service",
		nob: "bruksbetingelser",
		swe: "anvandarvillkor",
	},
	advertising: {
		da: "annoncering",
		en: "advertising",
		nob: "annonsering",
		swe: "annonsering",
	},
	privacyPolicy: {
		da: "privatlivspolitik",
		en: "privacy-policy",
		nob: "personvernpolicy",
		swe: "integritetspolicy",
	},
	business: {
		da: "business",
		en: "business",
		nob: "business",
		swe: "business",
	},
	businessTerms: {
		da: "business/vilkaar",
		en: "business/terms",
		nob: "business/vilkaar",
		swe: "business/villkor",
	},
	termsOfCompetition: {
		da: "konkurrencevilkaar",
		en: "terms-of-competition",
		nob: "konkurransevilkaar",
		swe: "tavlingsvillkor",
	},
	press: {
		da: "presse",
		en: "press",
		nob: "pressen",
		swe: "press",
	},
	contact: {
		da: "kontakt",
		en: "contact",
		nob: "kontakt",
		swe: "kontakt",
	},
	expertTerms: {
		da: "ekspertvilkaar",
		en: "expertterms",
		nob: "ekspertvilkaar",
		swe: "expertvillkor",
	},
	tradeTerms: {
		da: "handelsbetingelser",
		en: "trade-terms",
		nob: "handelsbetingelser",
		swe: "kopvillkor",
	},
	buyerProtection: {
		da: "koeberbeskyttelse",
		en: "buyer-protection",
		nob: "kjoeperbeskyttelse",
		swe: "kopskydd",
	},
};

/**
 * Returns the slug for a given page key and language from contentPageSlugMap.
 * @param pageKey The key in contentPageSlugMap (e.g., "business")
 * @param locale The language code (e.g., "da", "en", "nob")
 */
export function getContentPageSlug(pageKey: ContentPageSlug, locale: DefinedLocale): string {
	return contentPageSlugMap[pageKey][locale];
}

/**
 * Returns an array of all slugs for a given ContentPages key from contentPageSlugMap.
 * @param pageKey The key in contentPageSlugMap (e.g., "business")
 * @returns Array of slug strings for every listed locale
 */
export function getContentPageSlugInAllLanguages(pageKey: ContentPageSlug): string[] {
	return i18nLocales.map((locale) => contentPageSlugMap[pageKey][locale]);
}

/**
 * Helper function to convert info page slugs between locales
 */
export function getInfoPageSlugForLocale(infoSlug: string, newLocale: Locales): string {
	const mapping = Object.values(contentPageSlugMap).find((m) => Object.values(m).includes(infoSlug));
	if (mapping && mapping[newLocale]) {
		return mapping[newLocale];
	}
	return infoSlug;
}

/**
 * Converts a redirect URL (without locale) to match the current locale
 * @param redirectUrl The redirect URL from query params without locale (e.g., "/account/verify-email?token=123")
 * @param currentLocale The current locale (e.g., "da", "en", "nob")
 * @param allCategories Optional: Array of all categories for category path conversion
 * @returns The localized redirect URL with locale prefix
 */
export function getLocalizedRedirectUrl(redirectUrl: string, currentLocale: Locales, allCategories?: WebItemCategoryGroupProjection[] | null): string {
	const [pathOnly, ...queryParts] = redirectUrl.split("?");
	const queryString = queryParts.length > 0 ? `?${queryParts.join("?")}` : "";

	const cleanPath = pathOnly.replace(/^\//, "");
	const pathArray = cleanPath.split("/").filter(Boolean);

	if (pathArray.length === 0) {
		return `/${currentLocale}${queryString}`;
	}

	if (pathArray[0] === "info") {
		const infoSlug = pathArray.slice(1).join("/");
		const localizedSlug = getInfoPageSlugForLocale(infoSlug, currentLocale);
		return `/${currentLocale}/info/${localizedSlug}${queryString}`;
	}

	if (allCategories) {
		const category = allCategories.find((cat) => cat.languages.some((language) => language.path === cleanPath));

		if (category) {
			const localizedCategoryPath = getPathByLocale(currentLocale, category);
			return `/${currentLocale}/${localizedCategoryPath}${queryString}`;
		}
	}

	return `/${currentLocale}/${cleanPath}${queryString}`;
}
